/**
 * AriaLead — Tab "Lead & Chat".
 * Lista lead con privacy gating server-side (Free→0, Welcome→anonimi, Full+→completi).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Mail, Phone, MessageSquare, EyeOff, Lock, ArrowUpRight, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAriaCustomer } from "./AriaCustomerArea";

const TIER_BADGE = {
    free: { color: "#9EC3FF", label: "Free" },
    welcome: { color: "#86EFAC", label: "Welcome (anonimo)" },
    full: { color: "#A855F7", label: "Full" },
    pro: { color: "#A855F7", label: "Pro" },
    cinematic: { color: "#FF1A5C", label: "Cinematic" },
};

export default function AriaLead() {
    const { activeToken, planTier } = useAriaCustomer();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        if (!activeToken) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/widget/leads?widget_token=${activeToken}&limit=100`);
            setLeads(Array.isArray(data) ? data : (data?.items || []));
        } catch (e) { setError(formatApiError(e)); }
        setLoading(false);
    }, [activeToken]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="max-w-5xl" data-testid="tab-lead">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <h1 className="text-white text-[24px] mb-2" style={{ fontWeight: 300, letterSpacing: "-0.02em" }}>Lead & Conversazioni</h1>
                    <p className="text-white/65 text-[14px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {leads.length === 0 ? "Nessun lead ancora — Aria li raccoglierà man mano che le persone interagiscono con il widget."
                            : `${leads.length} lead raccolti dalla tua Aria.`}
                    </p>
                </div>
                <button type="button" onClick={load}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-white/75 text-[12px] transition"
                    data-testid="refresh-leads">
                    <RefreshCw size={11} /> aggiorna
                </button>
            </div>

            {/* Privacy banner per Free e Welcome */}
            {planTier === "free" && (
                <TierBanner
                    icon={Lock}
                    title="Aria Free non sblocca la visibilità dei lead"
                    message="Con il piano Free, i contatti sono raccolti ma li vede solo lo staff TotalKlick. Passa a Welcome (€19/mese) per leggere le conversazioni in modo anonimo, o a Full (€49/mese) per vedere nomi ed email completi."
                    cta="Passa a Welcome (15gg gratis)"
                    ctaHref="/aria/piani/welcome"
                />
            )}
            {planTier === "welcome" && (
                <TierBanner
                    icon={EyeOff}
                    title="Stai vedendo le conversazioni in modalità anonima"
                    message="Con Welcome leggi tutte le chat, ma i contatti personali (nome, email, telefono) sono nascosti per rispetto della privacy del visitatore. Passa a Full (€49/mese) per sbloccare i contatti completi."
                    cta="Sblocca i contatti (Full)"
                    ctaHref="/aria/piani/full"
                />
            )}

            {error && <div className="mb-4 p-3 rounded-xl text-[12px]" style={{ background: "rgba(255,26,92,0.1)", color: "#FF7AA3", border: "1px solid rgba(255,26,92,0.3)" }}>{error}</div>}

            <div className="rounded-2xl overflow-hidden" style={{ background: "rgba(15,18,28,0.6)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <table className="w-full text-[12.5px]">
                    <thead>
                        <tr className="text-left text-[10.5px] uppercase tracking-[0.22em] text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            <th className="px-5 py-3.5">Quando</th>
                            <th className="px-5 py-3.5">Nome</th>
                            <th className="px-5 py-3.5">Contatti</th>
                            <th className="px-5 py-3.5">Intent</th>
                            <th className="px-5 py-3.5">Tier</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan="5" className="px-5 py-10 text-center text-white/45">Caricamento…</td></tr>}
                        {!loading && leads.length === 0 && (
                            <tr><td colSpan="5" className="px-5 py-10 text-center">
                                <Inbox size={22} className="text-white/30 mx-auto mb-2" />
                                <div className="text-white/45 text-[12.5px]">Nessun lead da mostrare</div>
                            </td></tr>
                        )}
                        {leads.map((l, i) => {
                            const tier = l._plan_tier || planTier;
                            const isAnon = l._anonymized;
                            return (
                                <tr key={l.lead_id || i} className="border-t border-white/[0.04] hover:bg-white/[0.02]" data-testid={`lead-row-${i}`}>
                                    <td className="px-5 py-3.5 text-white/65 text-[11.5px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                        {l.created_at ? new Date(l.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                                    </td>
                                    <td className="px-5 py-3.5 text-white">
                                        {isAnon ? <span className="text-white/35 italic">— anonimo —</span> : (l.name || <span className="text-white/35">—</span>)}
                                    </td>
                                    <td className="px-5 py-3.5 text-white/75">
                                        {isAnon
                                            ? <span className="text-white/35 italic flex items-center gap-1"><EyeOff size={11} /> contatti nascosti</span>
                                            : (
                                                <div className="flex flex-col gap-0.5">
                                                    {l.email && <span className="inline-flex items-center gap-1 text-[12px]"><Mail size={10} className="text-white/45" /> {l.email}</span>}
                                                    {l.phone && <span className="inline-flex items-center gap-1 text-[12px]"><Phone size={10} className="text-white/45" /> {l.phone}</span>}
                                                    {!l.email && !l.phone && <span className="text-white/35">—</span>}
                                                </div>
                                            )}
                                    </td>
                                    <td className="px-5 py-3.5">
                                        <span className="text-[10.5px] uppercase tracking-[0.2em] px-2 py-0.5 rounded" style={{ background: "rgba(168,85,247,0.15)", color: "#C7A4FF", fontFamily: "JetBrains Mono, monospace" }}>
                                            {l.intent || "info"}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3.5">
                                        <span className="text-[10.5px] uppercase tracking-[0.2em] px-2 py-0.5 rounded" style={{ background: (TIER_BADGE[tier]?.color || "#9EC3FF") + "20", color: TIER_BADGE[tier]?.color || "#9EC3FF", fontFamily: "JetBrains Mono, monospace" }}>
                                            {TIER_BADGE[tier]?.label || tier}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function TierBanner({ icon: Icon, title, message, cta, ctaHref }) {
    return (
        <div className="mb-6 p-5 rounded-2xl flex items-start gap-4" style={{ background: "linear-gradient(120deg, rgba(168,85,247,0.12), rgba(255,26,92,0.08))", border: "1px solid rgba(168,85,247,0.35)" }} data-testid="tier-banner">
            <div className="w-10 h-10 rounded-xl grid place-items-center flex-none" style={{ background: "rgba(168,85,247,0.2)" }}>
                <Icon size={15} className="text-[#C7A4FF]" />
            </div>
            <div className="flex-1">
                <div className="text-white text-[14px] mb-1" style={{ fontWeight: 500 }}>{title}</div>
                <div className="text-white/65 text-[12.5px] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>{message}</div>
                <Link to={ctaHref} className="inline-flex items-center gap-1.5 text-[12.5px] text-white/95 hover:text-white" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>
                    {cta} <ArrowUpRight size={11} />
                </Link>
            </div>
        </div>
    );
}
